import { useState } from "react";
import { useApolloClient } from "@apollo/client";
import { DocumentType, gql } from "__generated__/gql";
import { useCurrentBrandData } from "utils/currentUserHooks";
import ToastError from "utils/ToastError";
import asyncVoidHandler from "utils/asyncVoidHandler";
import type { ValidatedData } from "utils/xlsxImportExport/XlsxImportWorkBook";
import {
  ColumnFormatsByColumnBySheet,
  getWorkBookExport,
} from "utils/xlsxImportExport/export";
import ExportButton from "components/Shared/ExportButton";
import {
  CommentRow,
  WorkSheetDefinitions,
  WORKSHEET_DEFINITIONS,
} from "utils/xlsxDefinitions/comments";
import { assertPresence } from "utils/assertions";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import { DATE_TIME, dateColumn } from "utils/xlsxUtils";
import { CommentFilter } from "__generated__/gql/graphql";
import { COMMENTS_TITLE } from "components/Comments/utils";

const query = gql(/* GraphQL */ `
  query ExportComments($brandId: UUID!, $filter: CommentFilter) {
    commentsForBrand(
      brandId: $brandId
      filter: $filter
      orderBy: CREATED_AT_DESC
      first: 1000
    ) {
      totalCount
      nodes {
        body
        createdAt
        user {
          fullName
          email
        }
        brandRetailerComment {
          brandRetailer {
            retailer {
              nameWithUniqueSuffix
            }
          }
        }
      }
    }
  }
`);

const columnFormats: ColumnFormatsByColumnBySheet<WorkSheetDefinitions> = {
  comments: {
    postedAt: {
      ...dateColumn,
      columnInfo: { wch: 25 },
      columnFormat: DATE_TIME,
    },
    relatedItemName: { columnInfo: { wch: 30 } },
    authorName: { columnInfo: { wch: 30 } },
    authorEmail: { columnInfo: { wch: 35 } },
    body: { columnInfo: { wch: 100 } },
  },
};

function getSheetData(
  customersData: NonNullable<
    DocumentType<typeof query>["commentsForBrand"]
  >["nodes"],
): ValidatedData<WorkSheetDefinitions> {
  const comments = customersData.map<CommentRow>((comment) => {
    assertPresence(comment.user);

    let relatedItemName: string | null | undefined;

    if (comment.brandRetailerComment) {
      relatedItemName =
        comment.brandRetailerComment.brandRetailer?.retailer
          ?.nameWithUniqueSuffix;
    }

    return {
      authorEmail: comment.user.email,
      authorName: comment.user.fullName,
      body: comment.body,
      postedAt: comment.createdAt,
      relatedItemName,
    };
  });
  return { comments };
}

type Props = {
  filter?: CommentFilter;
};

function ExportCommentsButton({ filter }: Props) {
  const [isLoading, setIsLoading] = useState(false);

  const client = useApolloClient();
  const brandData = useCurrentBrandData();
  const { showSuccessToast, showErrorToast, showToast } =
    useAsyncCompleteToast();

  const handleClick = async () => {
    setIsLoading(true);

    try {
      const { default: xlsx } = await import("xlsx");

      const { data } = await client.query({
        query,
        variables: brandData?.id
          ? { brandId: brandData.id, filter }
          : undefined,
        fetchPolicy: "no-cache",
      });

      assertPresence(data.commentsForBrand);

      if (!data.commentsForBrand.totalCount) {
        throw new ToastError(`No ${COMMENTS_TITLE} were found to export.`);
      }

      if (data.commentsForBrand.totalCount > 1000) {
        showToast({
          title: `Too many ${COMMENTS_TITLE} to export`,
          description:
            `Export will contain only the first 1,000 ${COMMENTS_TITLE}. ` +
            `Please apply a filter to reduce the number of ${COMMENTS_TITLE}.`,
          status: "warning",
          duration: 10000,
        });
      }

      const workSheetsDataByKey = getSheetData(data.commentsForBrand.nodes);

      const workBook = await getWorkBookExport(
        WORKSHEET_DEFINITIONS,
        workSheetsDataByKey,
        columnFormats,
      );

      xlsx.writeFile(
        workBook,
        `${COMMENTS_TITLE} for ${brandData?.name || ""}.xlsx`,
      );

      showSuccessToast(`${COMMENTS_TITLE} exported successfully.`);
    } catch (e) {
      console.error(e);
      showErrorToast(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ExportButton
      onClick={asyncVoidHandler(handleClick)}
      isLoading={isLoading}
      variant="outline"
    />
  );
}

export default ExportCommentsButton;
